import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { IonIcon } from '@ionic/react';
import { closeOutline } from 'ionicons/icons';

type Size = 'sm' | 'md' | 'lg';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  size?: Size;
  children: React.ReactNode;
  /** Extra classes appended to the card box (e.g. `space-y-4`, `flex flex-col`). */
  className?: string;
  /** Replaces the default surface classes (`bg-card text-card-foreground`). Pass when migrating non-themed modals. */
  surfaceClassName?: string;
  /** z-index utility class for the outer fixed layer. Defaults to `z-[60]`. */
  zIndexClass?: string;
  /** Hide the built-in top-right X close button. Defaults to `false` (X shown). */
  hideCloseButton?: boolean;
}

const SIZE_CLASS: Record<Size, string> = {
  sm: 'max-w-[22rem]',
  md: 'max-w-md',
  lg: 'max-w-lg',
};

// Portals to document.body so the modal escapes any ancestor (e.g. IonModal)
// whose transform creates a containing block for fixed-positioned descendants.
// Without this, a modal opened from inside a sheet centers in the sheet, not
// the viewport. Because it lands at <body> level it also escapes the desktop
// iPhone frame; the `hp-overlay-layer` class lets global.scss pin the layer to
// the frame's rect in framed mode.
export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  size = 'sm',
  children,
  className,
  surfaceClassName = 'bg-card text-card-foreground',
  zIndexClass = 'z-[60]',
  hideCloseButton = false,
}) => {
  // When opened inside an IonModal/IonPopover/etc., Ionic's overlay focus trap
  // sees inputs inside our portaled modal as "outside the topmost overlay" and
  // forcibly steals focus back, breaking typing. `ion-disable-focus-trap` is
  // Ionic's official opt-out (see @ionic/core overlays.ts: trapKeyboardFocus
  // returns early when the topmost overlay has this class). Apply it while
  // open, restore on close.
  //
  // The class has to land on the exact element Ionic considers topmost, so this
  // mirrors its `getPresentedOverlay`: every overlay tag (not just ion-modal —
  // an action sheet or popover can be the one trapping), narrowed to the ones
  // actually presented. Inline overlays stay in the DOM while closed, so "last
  // ion-modal in document order" is usually a *hidden* one, and tagging it
  // leaves the real overlay still stealing focus.
  useEffect(() => {
    if (!isOpen) return;
    const overlays = Array.from(
      document.querySelectorAll<HTMLElement>(
        'ion-alert,ion-action-sheet,ion-loading,ion-modal,ion-picker-legacy,ion-popover',
      ),
    ).filter(
      (el) =>
        ((el as HTMLElement & { overlayIndex?: number }).overlayIndex ?? 0) > 0 &&
        !el.classList.contains('overlay-hidden'),
    );
    const topMost = overlays[overlays.length - 1];
    if (!topMost) return;
    const hadClass = topMost.classList.contains('ion-disable-focus-trap');
    topMost.classList.add('ion-disable-focus-trap');
    return () => {
      if (!hadClass) topMost.classList.remove('ion-disable-focus-trap');
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    // stopPropagation guards against clicks leaking through the React tree to
    // a clickable ancestor in the component that rendered this Modal (the DOM
    // tree is escaped via the portal, but React's synthetic events still bubble
    // along the component hierarchy). Without this, a clickable ancestor's
    // onClick can swallow clicks meant for inputs inside the modal.
    <div
      className={`hp-overlay-layer fixed inset-0 ${zIndexClass}`}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Visual dark backdrop. Purely cosmetic — the centering wrapper below is
          a later sibling covering the full viewport (`inset-0`), so it, not this
          layer, receives outside clicks. The click-to-close handler therefore
          lives on the wrapper; putting it here would make it unreachable. */}
      <div className="absolute inset-0 bg-black/40" />
      {/* px-6 (24px) on the centering wrapper is the screen-edge breathing
          room on phones where `w-full` would otherwise stretch the card
          edge-to-edge. Padding here (instead of margin on the card) ensures
          `w-full` resolves to "screen width minus the gutter" — margins on
          a w-full child of a flex container push the box off-screen instead
          of shrinking it. Bump this once if you ever want more (or less).
          Wider viewports hit `max-w-*` first and the padding is moot.
          onClick closes on any click that lands outside the card (the card
          stops propagation), giving click-outside-to-dismiss. */}
      <div
        className="absolute inset-0 flex items-center justify-center px-6"
        onClick={onClose}
      >
        <div
          className={`relative ${surfaceClassName} rounded-xl shadow-lg w-full ${SIZE_CLASS[size]} p-6 max-h-[90%] overflow-y-auto ${className ?? ''}`}
          onClick={(e) => e.stopPropagation()}
        >
          {!hideCloseButton && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-black/5 active:scale-95 transition"
            >
              <IonIcon icon={closeOutline} className="text-xl" />
            </button>
          )}
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
};
